import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  LinearProgress,
  Stack,
  TextField,
  Typography,
  alpha
} from '@mui/material';
import {
  Rule as RuleIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import api from 'api/client';

const accent = '#f97316';
const cardSx = {
  border: '1px solid',
  borderColor: alpha(accent, 0.35),
  borderRadius: 3,
  boxShadow: 'none'
};

const buttonSx = {
  bgcolor: 'var(--fz-surface-1, #111827)',
  color: accent,
  border: `1px solid ${accent}`,
  '&:hover': { bgcolor: 'var(--fz-surface-0, #0b1220)', borderColor: '#fb923c' }
};

const defaultPolicy = {
  critical_days: 3,
  high_days: 7,
  medium_days: 14,
  low_days: 30,
  due_soon_days: 7
};

const fieldMeta = [
  { key: 'critical_days', label: 'Critical SLA (days)', hint: 'Maximum remediation time for critical cases.' },
  { key: 'high_days', label: 'High SLA (days)', hint: 'Maximum remediation time for high priority cases.' },
  { key: 'medium_days', label: 'Medium SLA (days)', hint: 'Maximum remediation time for medium priority cases.' },
  { key: 'low_days', label: 'Low SLA (days)', hint: 'Maximum remediation time for low priority cases.' },
  { key: 'due_soon_days', label: 'Due Soon Window (days)', hint: 'Cases due within this window are flagged as due-soon.' }
];

function toInteger(value, fallback) {
  const parsed = Number(value);
  if (Number.isNaN(parsed)) return fallback;
  return Math.min(365, Math.max(1, Math.floor(parsed)));
}

function parseError(error, fallback) {
  const payload = error?.response?.data;
  if (!payload) return error?.message || fallback;
  if (typeof payload === 'string') return payload;
  if (payload.detail) return payload.detail;
  const key = Object.keys(payload)[0];
  if (key) {
    const val = payload[key];
    if (Array.isArray(val)) return val[0];
    if (typeof val === 'string') return val;
  }
  return fallback;
}

const AdminSlaPolicyPage = () => {
  const [policy, setPolicy] = useState(defaultPolicy);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadPolicy = useCallback(async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const { data } = await api.get('/api/admin/settings/sla-policy/');
      setPolicy({
        critical_days: toInteger(data?.critical_days, defaultPolicy.critical_days),
        high_days: toInteger(data?.high_days, defaultPolicy.high_days),
        medium_days: toInteger(data?.medium_days, defaultPolicy.medium_days),
        low_days: toInteger(data?.low_days, defaultPolicy.low_days),
        due_soon_days: toInteger(data?.due_soon_days, defaultPolicy.due_soon_days)
      });
    } catch (err) {
      setError(parseError(err, 'Failed to load SLA policy.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPolicy();
  }, [loadPolicy]);

  const savePolicy = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        critical_days: toInteger(policy.critical_days, defaultPolicy.critical_days),
        high_days: toInteger(policy.high_days, defaultPolicy.high_days),
        medium_days: toInteger(policy.medium_days, defaultPolicy.medium_days),
        low_days: toInteger(policy.low_days, defaultPolicy.low_days),
        due_soon_days: toInteger(policy.due_soon_days, defaultPolicy.due_soon_days)
      };
      const { data } = await api.put('/api/admin/settings/sla-policy/', payload);
      setPolicy({
        critical_days: toInteger(data?.critical_days, payload.critical_days),
        high_days: toInteger(data?.high_days, payload.high_days),
        medium_days: toInteger(data?.medium_days, payload.medium_days),
        low_days: toInteger(data?.low_days, payload.low_days),
        due_soon_days: toInteger(data?.due_soon_days, payload.due_soon_days)
      });
      setSuccess('SLA policy updated successfully.');
    } catch (err) {
      setError(parseError(err, 'Failed to save SLA policy.'));
    } finally {
      setSaving(false);
    }
  };

  const preview = useMemo(() => {
    return [
      { label: 'Critical', days: toInteger(policy.critical_days, 3), color: '#dc2626' },
      { label: 'High', days: toInteger(policy.high_days, 7), color: '#f97316' },
      { label: 'Medium', days: toInteger(policy.medium_days, 14), color: '#f59e0b' },
      { label: 'Low', days: toInteger(policy.low_days, 30), color: '#16a34a' }
    ];
  }, [policy]);

  return (
    <Stack spacing={2.5}>
      <Card sx={cardSx}>
        <CardContent sx={{ py: 2.2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 8 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: accent,
                    bgcolor: alpha(accent, 0.12)
                  }}
                >
                  <RuleIcon fontSize="small" />
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  SLA Policy
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Configure default remediation due-date windows used when creating new cases.
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                <Button variant="contained" startIcon={<RefreshIcon />} onClick={loadPolicy} disabled={loading || saving} sx={buttonSx}>
                  Reload
                </Button>
                <Button variant="contained" startIcon={<SaveIcon />} onClick={savePolicy} disabled={loading || saving} sx={buttonSx}>
                  Save
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
        {(loading || saving) && <LinearProgress sx={{ '& .MuiLinearProgress-bar': { backgroundColor: accent } }} />}
      </Card>

      {error && <Alert severity="error">{error}</Alert>}
      {success && <Alert severity="success">{success}</Alert>}

      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, md: 7 }}>
          <Card sx={cardSx}>
            <CardHeader title="Policy Values" titleTypographyProps={{ variant: 'h6', fontWeight: 700 }} />
            <Divider />
            <CardContent>
              <Grid container spacing={2}>
                {fieldMeta.map((field) => (
                  <Grid size={{ xs: 12, sm: 6 }} key={field.key}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label={field.label}
                      value={policy[field.key]}
                      onChange={(event) => setPolicy((prev) => ({ ...prev, [field.key]: event.target.value }))}
                      inputProps={{ min: 1, max: 365 }}
                      helperText={field.hint}
                    />
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 5 }}>
          <Card sx={cardSx}>
            <CardHeader title="Policy Preview" titleTypographyProps={{ variant: 'h6', fontWeight: 700 }} />
            <Divider />
            <CardContent>
              <Stack spacing={1.4}>
                {preview.map((item) => (
                  <Box key={item.label} sx={{ border: `1px solid ${alpha(item.color, 0.28)}`, borderRadius: 2, p: 1.2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: item.color }}>
                      {item.label}
                    </Typography>
                    <Typography variant="body2">Default due date in {item.days} day(s)</Typography>
                  </Box>
                ))}
                <Box sx={{ border: '1px solid rgba(100, 116, 139, 0.3)', borderRadius: 2, p: 1.2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#475569' }}>
                    Due Soon Window
                  </Typography>
                  <Typography variant="body2">Cases due within {toInteger(policy.due_soon_days, 7)} day(s) are highlighted.</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Stack>
  );
};

export default AdminSlaPolicyPage;
